<template>
  <section class="main">
    <v-page-header :title="getPageTile" />

    <div class="grid-container">
      <div class="grid-x">
        <div class="cell medium-9 medium-offset-1 large-11 large-offset-0">
          <div class="wrapper">
            <v-action-buttons
              :items="getActionButtons"
              :isDisabled="projectFormDisabled"
              @actionClicked="onClickAction"
            />

            <v-form-message type="error" id="form-error" :message="projectObjectError" :autoCloseTimeout="5000" />
            <v-form-message type="success" id="form-success" :message="projectObjectSuccess" />

            <v-form-message
              type="default"
              :message="objectCheckTOK"
              :autoCloseTimeout="0"
              :showActionButtons="true"
              @onClickAction="onClickDialogAction"
            />
            
            <div class="grid-x grid-margin-x">
                
                <!-- Column 1 -->     
                <div class="cell medium-12 large-6">
                  
                    <div class="box">
                      
                      <div class="grid-x grid-margin-x">
                        
                        <div class="cell small-3">
                          <h2>{{ $t('form.project.title.project') }}</h2>
                        </div>
                        <template v-if="$store.getters.userIsAdmin">
                          <div class="cell small-6 project-detail-status">
                            <template v-if="object.location.risk">
                              <p v-if="object.location.risk.is_valid && object.location.risk.decided_budget">
                                <strong >
                                  {{ $t('location.risk.limit') }}: {{ $helpers.formateCurrency(object.location.risk.decided_budget)}}
                                </strong>
                              </p>
                              <p v-else>
                                <strong class="u-color--alert">
                                  {{ $t('location.risk.not_valid') }}
                                </strong>
                              </p>
                            </template>
                            <template v-else>
                              <p>
                                <strong class="u-color--alert">{{ $t('location.risk.no_risk_number') }}</strong>
                              </p>
                            </template>
                          </div>
                        </template>
                        <div class="cell small-3 text-right">
                          <div class="project-detail-status">
                            <span
                              class="status-icon"
                              :class="'status-icon--' + object.status"
                            >{{ $t('status.' + object.status) }}</span>
                          </div>
                        </div>
                        
                      </div>
                      <div class="grid-x grid-margin-x">
                        
                        <div class="cell medium-6">
                          
                          <div class="form-block">
                            <div
                              class="form-group"
                              :class="{ 'form-group--error' : $v.object.location_id.$error }"
                            >
                              <label for="form-field-location">{{ $t('form.project.location') }}</label>
                              <v-select-location
                                :placeholder="$t('form.project.location.plc')"
                                v-model="$v.object.location_id.$model"
                                :tabindex="1"
                                :customerId="$route.params.customer"
                                :isStatic="$store.getters.userIsProjectManager"
                                @change="onChangeLocation"
                              />
                            </div>
                            
                            <p
                              class="form-group form-group--info"
                            >{{ $t('form.project.project_number') }}: {{ object.project_number }}</p>
                            <p
                              class="form-group form-group--info"
                            >{{ $t('createdon_date') }}: {{ object.createdon.date | moment($helpers.dateFormat.toUpperCase()) }}</p>
                          </div> <!-- .cell -->
                          
                        </div> <!-- .grid-x -->
                        
                        <div class="cell medium-6">
                          
                          <div class="form-block">
                            
                            <div class="form-group" :class="{ 'form-group--error' : $v.object.account_id.$error }">
                              <label for="form-field-pm">{{ $t('form.project.pm') }}</label>
                              <v-select-user
                                :placeholder="$t('form.project.pm.plc')"
                                v-model="$v.object.account_id.$model"
                                :tabindex="1"
                                :userType="$helpers.usertypes.pm"
                                :locationId="$v.object.location_id.$model"
                                :customerId="$route.params.customer"
                                :isStatic="$store.getters.userIsProjectManager"
                                @change="onChangeAccount"
                              />
                            </div>
                            <p
                              class="form-group"
                            >{{ $t('mobile') }}: {{ object.account.profile.communication.mobile }}</p>
                            <p
                              class="form-group"
                            >{{ $t('phone') }}: {{ object.account.profile.communication.phone }}</p>
                            <p
                              class="form-group"
                            >{{ $t('mail') }}: {{ object.account.profile.communication.email }}</p>
                          </div>
                          
                        </div>
                        
                      </div> <!-- .cell -->
                      
                    </div><!-- .box -->
                    
                    <!-- Start of address form fields -->
                    <section class="box">
                      <h2 class="u-extra-small-margin--bottom">{{ $t('form.project.title.location') }}</h2>
                      <div
                        class="callout warning text-center"
                      >{{ $t('project.message.onestreetperproject')}}</div>

                      <div class="grid-x grid-margin-x" id="address-form-fields">
                        <div class="cell medium-6">
                          <div class="form-block">
                            <div class="grid-x grid-margin-x">
                              <div class="cell xxlarge-5 medium-4">
                                <div
                                  class="form-group"
                                  :class="{ 'form-group--error' : $v.object.address.zipcode.$error }"
                                >
                                  <input
                                    type="text"
                                    id="form-field-zipcode"
                                    name="zipcode"
                                    class="form-control"
                                    :class="{ 'loading-spinner--input' : geocodeLoading }"
                                    :placeholder="$t('form.zipcode.plc')"
                                    v-model="$v.object.address.zipcode.$model"
                                    :tabindex="3"
                                    @keyup="onChangeZipcode"
                                  />
                                </div>
                              </div>
                              <div class="cell xxlarge-7 medium-8">
                                <div
                                  class="form-group"
                                  :class="{ 'form-group--error' : $v.object.address.city.$error }"
                                >
                                  <input
                                    type="text"
                                    id="form-field-city"
                                    name="city"
                                    class="form-control"
                                    :class="{ 'loading-spinner--input' : geocodeLoading }"
                                    :placeholder="$t('form.city.plc')"
                                    v-model="$v.object.address.city.$model"
                                    :tabindex="4"
                                    @keyup="onChangeCity"
                                  />
                                </div>
                              </div>
                            </div>
                            <div class="grid-x grid-margin-x">
                              <div class="cell xxlarge-12 large-4">
                                <div
                                  class="form-group form-group__extra"
                                  v-if="geocodeObject.zipcode.length > 1"
                                >
                                  <select
                                    name="zipcode_list"
                                    class="form-control form-control--select"
                                    @change="onSelectZipcode"
                                  >
                                    <option value>{{ $t('form.zipcode.choose')}}</option>
                                    <option
                                      v-for="(item, index) in geocodeObject.zipcode"
                                      :key="index + '_zipcode'"
                                      :value="item.zipcode"
                                    >{{ item.zipcode }}</option>
                                  </select>
                                </div>
                              </div>
                              <div class="cell xxlarge-12 large-8">
                                <div
                                  class="form-group form-group__extra"
                                  v-if="geocodeObject.city.length > 1"
                                >
                                  <select
                                    name="city_list"
                                    class="form-control form-control--select"
                                    @change="onSelectCity"
                                  >
                                    <option value>{{ $t('form.city.choose')}}</option>
                                    <option
                                      v-for="(item, index) in geocodeObject.city"
                                      :key="index + '_city'"
                                      :value="item.city"
                                    >{{ item.city }}</option>
                                  </select>
                                </div>
                              </div>
                            </div>
                            <div class="grid-x grid-margin-x">
                              <div class="cell medium-8">
                                <div
                                  class="form-group"
                                  :class="{ 'form-group--error' : $v.object.address.street.$error }"
                                >
                                  <input
                                    type="text"
                                    id="form-field-street"
                                    name="street"
                                    class="form-control"
                                    :placeholder="$t('form.street.plc')"
                                    v-model="$v.object.address.street.$model"
                                    :tabindex="5"
                                  />
                                </div>
                              </div>
                              <div class="cell medium-4">
                                <div class="form-group">
                                  <input
                                    type="text"
                                    id="form-field-housenumber"
                                    name="housenumber"
                                    class="form-control"
                                    :placeholder="$t('form.housenumber.plc')"
                                    v-model="object.address.housenumber"
                                    :tabindex="6"
                                  />
                                </div>
                              </div>
                            </div>
                            <div class="form-group">
                              <label>{{ $t('form.project.site_comments') }}</label>
                              <textarea
                                id="form-field-site-comments"
                                name="site_comments"
                                class="form-control form-control--textarea"
                                :placeholder="$t('form.project.site_comments.plc')"
                                v-model="object.site_comments"
                                :tabindex="7"
                              ></textarea>
                            </div>
                            <div class="form-group">
                              <label>{{ $t('form.project.site_contact_label') }}</label>
                              <textarea
                                id="form-field-site-contact-info"
                                name="site_contact_info"
                                class="form-control--textarea"
                                :placeholder="$t('form.project.site_contact_info.plc')"
                                v-model="object.site_contact_info"
                                :tabindex="8"
                              ></textarea>
                            </div>
                            <div
                              class="form-group"
                              :class="{ 'form-group--error' : $v.object.internal_number.$error }"
                            >
                              <textarea
                                id="form-field-internal-number"
                                name="internal_number"
                                class="form-control form-control--textarea form-control--highlighted"
                                :placeholder="$t('form.project.internal_number.plc')"
                                v-model="$v.object.internal_number.$model"
                                :tabindex="9"
                              ></textarea>
                            </div>
                          </div>
                        </div>
                        <div class="cell medium-6">
                          <div class="form-block">
                            <div class="form-group">
                              <div class="form-control form-control--maps">
                                <v-google-map
                                  :address="object.address.street + ' ' + object.address.housenumber + ' ' + object.address.city + ' ' + object.address.zipcode"
                                  :resize="!isLoading"
                                  @marker="getMarker"
                                  :tabindex="-1"
                                  :zoom="10"
                                  :zoomControl="true"
                                  :panControl="true"
                                  :useFitBounds="false"
                                />
                              </div>
                            </div>

                          </div>
                        </div>
                      </div>

                      <div class="grid-x grid-margin-x">
                          <div class="cell medium-6">
                            <div class="form-block">
                              <div
                              class="form-group"
                              :class="{ 'form-group--error' : $v.object.earliest_start.$error }"
                            >
                              <label for="form-field-earliest-start">{{ $t('form.project.earliest_start') }}</label>
                              <p class="form-help">{{ $t('form.project.earliest_start.help') }}</p>
                              <v-datepicker
                                id="form-field-earliest-start"
                                class="form-control form-control--date"
                                :placeholder="$t('form.project.earliest_start.plc')"
                                :format="$helpers.dateFormat"
                                v-model="$v.object.earliest_start.$model"
                                :disabled-dates="earliestStartDisabledDates"
                                :language="de"
                                :monday-first="true"
                                :tabindex="10"
                              />
                              <p v-if="$v.object.earliest_start.$error" class="form-help u-error">
                                {{ earliestStartError }}
                              </p>
                           
                            </div>
                            <div class="form-group">
                              <label for="form-field-earliest-start-comment">{{ $t('form.project.earliest_start_comment') }}</label>
                              <input
                                type="text"
                                id="form-field-earliest-start-comment"
                                name="earliest_start_comment"
                                class="form-control"
                                maxlength="255"
                                :placeholder="$t('form.project.earliest_start_comment.plc')"
                                v-model="object.earliest_start_comment"
                                :tabindex="11"
                              />
                            </div>   
                          </div>
                        </div>
                      </div>
                    </section><!-- .box -->
                    <!-- End of address form fields -->
                    
                    <section class="box" v-if="$store.getters.userIsAdmin">
                        <div class="grid-x grid-margin-x">
                          <div class="cell small-12">
                                <h2>{{ $t('project.internalarea.title') }}</h2>
                          </div>
                          <div class="cell small-12">
                            <div class="form-block">
                                <div class="form-group">
                                    <label>{{ $t('project.internal_note') }}</label>
                                    <textarea
                                    id="form-field-internal-note"
                                    name="internal_note"
                                    class="form-control form-control--textarea"
                                    v-model="object.internal_note"
                                    :tabindex="12"
                                    ></textarea>
                                </div>
                            </div>
                           </div>
                        </div>
                    </section>
                    
                </div> <!-- Column 1 .cell -->
                
                <!-- Column 2 -->     
                 <div class="cell medium-12 large-6">
                   
                      <div class="box">
                        <div class="grid-x grid-margin-x grid-padding-x">
                          <div class="cell medium-7">
                            <div class="loading-wrapper" :class="{ 'is-loading' : layersLoading }">
                              <div class="loading-message">{{ this.$t('results_loading') }}</div>
                              <div class="layer-stats layer-stats--overview">
                                <div
                                  class="layer-stats--overview__item layer-stats--overview__item--total"
                                >
                                  <strong>{{ $helpers.formatNumber(layersStats.total) }}</strong>
                                  {{ $t('qm_total') }}
                                </div>
                                <div
                                  class="layer-stats--overview__item layer-stats--overview__item--finished"
                                >
                                  <strong>{{ $helpers.formatNumber(layersStats.finished) }}</strong>
                                  {{ $t('status.finished') }}
                                </div>
                                <div
                                  class="layer-stats--overview__item layer-stats--overview__item--open"
                                >
                                  <strong>{{ $helpers.formatNumber(layersStats.open) }}</strong>
                                  {{ $t('status.open') }}
                                </div>
                              </div>
                            </div>
                          </div>
                          <div class="cell medium-5">
                            <div class="form-block">
                              <div class="form-group form-group--check">
                                <label class="large">
                                  <input
                                    type="checkbox"
                                    name="layer_hole"
                                    value="1"
                                    v-model="object.layer_hole"
                                  />
                                  <i></i>
                                  {{ $t('form.project.layer_hole') }}
                                </label>
                              </div>
                              <div class="form-group form-group--check">
                                <label class="large">
                                  <input
                                    type="checkbox"
                                    name="layer_tok"
                                    value="1"
                                    v-model="object.layer_tok"
                                  />
                                  <i></i>
                                  {{ $t('form.project.layer_tok') }}
                                </label>
                              </div>
                              <div class="form-group form-group--check">
                                <label class="large">
                                  <input
                                    type="checkbox"
                                    name="layer_installation"
                                    value="1"
                                    v-model="object.layer_installation"
                                    @change="onChangeLayerInstallation"
                                  />
                                  <i></i>
                                  {{ $t('form.project.layer_installation') }}
                                </label>
                              </div>
                              <div
                                class="form-group form-group--check"
                                v-show="this.object.layer_installation"
                              >
                                <label class="large">{{ $t('form.project.layer_width') }}</label>
                                <div class="form-group-toggle">
                                  <div class="grid-container full">
                                    <div class="grid-x">
                                      <div class="cell medium-4 large-5">
                                        <input
                                          type="text"
                                          id="form-element-layer-width-min"
                                          name="layer_width_min"
                                          class="form-control form-control--small"
                                          v-model="object.layer_width_min"
                                        />
                                      </div>
                                      <div class="cell medium-3 large-auto">
                                        <div
                                          class="form-control form-control--small form-control--seperator"
                                        >{{ $t('till') }}</div>
                                      </div>
                                      <div class="cell medium-4 large-5">
                                        <input
                                          type="text"
                                          id="form-element-layer-width-min"
                                          name="layer_width_max"
                                          class="form-control form-control--small"
                                          v-model="object.layer_width_max"
                                        />
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div><!-- .box -->
                   
                      <div class="box">
                        <h2>{{ $t('form.project.title.default_layers') }}</h2>
                        <div
                          class="layers layers--overview loading-wrapper"
                          :class="{ 'is-loading' : layersLoading }"
                        >
                          <div class="loading-message">{{ this.$t('results_loading') }}</div>
                          <div v-for="(layer, index) in sortLayers" :key="index + '_sortLayers'">
                            <template v-if="layer.is_default">
                              <div class="layers--overview__item">
                                <v-project-layer
                                  :layer="layer"
                                  :projectId="object.id"
                                  v-model="$v.object.layers.$each.$iter[layer.id]"
                                  @change="onChangeLayer"
                                  @changeField="onUpdateLayersStats"
                                />
                              </div>
                            </template>
                          </div>
                        </div>
                      </div><!-- .box -->
                   
                       <div class="box">
                         
                          <div class="box__title">
                            <h2>{{ $t('form.project.title.addition_layers') }}</h2>
                            <span
                              v-html="$t('form.project.title.addition_layers.total', { total : $helpers.formatNumber(layersStats.layers.addition) } )"
                            ></span>
                          </div>
                          
                          <div
                            class="layers layers--overview loading-wrapper"
                            :class="{ 'is-loading' : layersLoading }"
                          >
                          
                            <div class="loading-message">{{ this.$t('results_loading') }}</div>
                            
                            <div v-for="(layer, index) in sortLayers" :key="index + '_sort_layers_2'">
                              <template v-if="!layer.is_default">
                                <div class="layers--overview__item">
                                  <v-project-layer
                                    :layer="layer"
                                    :projectId="object.id"
                                    v-model="$v.object.layers.$each.$iter[layer.id]"
                                    @change="onChangeLayer"
                                    @changeField="onUpdateLayersStats"
                                  />
                                </div>
                              </template>
                            </div>
                            
                          </div><!-- .layers -->
                          
                        </div><!-- .box -->
                   
                 </div> <!-- Column 2 .cell -->   
                    
            </div>
            
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import PageHeader from "./../partials/PageHeader";
import PageLoading from "./../partials/PageLoading";
import ActionButtons from "./../partials/Foundation/ActionButtons";
import Navigation from "./../customers/Navigation";
import FormMessage from "./../partials/Forms/FormMessage";
import { required, requiredIf } from "vuelidate/lib/validators";
import Datepicker from "vuejs-datepicker";
import { de } from "vuejs-datepicker/dist/locale";

import SelectLocation from "./../partials/Forms/SelectLocation";
import SelectUser from "./../partials/Forms/SelectUser";

import ProjectLayer from "./../customers/projects/ProjectLayer";

import GoogleMap from "./../partials/GoogleMap";

import _ from "lodash";

export default {
  name: "ProjectCreate",
  data() {
    return {
      de: de,
      loading: false,
      loadingError: null,
      customerObject: {
        id: 0,
      },
      objectCheckTOK: null,
      objectCheckTOKEmptyWanted: null,
      projectFormDisabled: false,
      projectObjectError: null,
      projectObjectSuccess: null,
      object: {
        id: 0,
        location_id: 0,
        account_id: 0,
        location: {
            risk: ''
        },
        account: {
          profile: {
            communication: {
              email: "",
              phone: "",
              mobile: "",
            },
          },
        },
        lat: "",
        lon: "",
        customer_id: this.$route.params.customer,
        createdon: {
          date: this.$moment().format("YYYY-MM-DD hh:mm:ss"),
        },
        project_number: "n/a",
        site_comments: "",
        site_contact_info: "",
        status: "open",
        internal_number: "",
        internal_note: "",
        earliest_start: null,
        earliest_start_comment: "",
        address: {
          city: "",
          housenumber: "",
          region: "",
          street: "",
          zipcode: "",
        },
        layers: {},
        layer_hole: 0,
        layer_tok: 0,
        layer_installation: 0,
        layer_width_min: 0,
        layer_width_max: 0,
      },
      layers: [],
      sortLayers: [],
      layersLoading: false,
      layersStats: {
        total: 0,
        finished: 0,
        open: 0,
        layers: {
          default: 0,
          addition: 0,
        },
      },
      geocodeLoading: false,
      geocodeObject: {
        zipcode: [],
        city: [],
      },
    };
  },
  components: {
    vPageHeader: PageHeader,
    vPageLoading: PageLoading,
    vActionButtons: ActionButtons,
    vNavigation: Navigation,
    vFormMessage: FormMessage,
    vSelectLocation: SelectLocation,
    vSelectUser: SelectUser,
    vProjectLayer: ProjectLayer,
    vGoogleMap: GoogleMap,
    vDatepicker: Datepicker,
  },
  computed: {
    earliestStartDisabledDates: function () {
      return {
        to: this.$helpers.firstSelectableEarliestStart(new Date()),
        days: [6, 0],
      };
    },
    earliestStartError: function () {
      if (!this.$v.object.earliest_start.required) {
        return this.$t("form.project.earliest_start.required");
      }
      return this.$t("form.project.earliest_start.too_soon");
    },
    isLoading: function () {
      return this.loading;
    },
    hasLoadingError: function () {
      return this.loadingError !== null;
    },
    isViewMethod: function () {
      return this.$route.meta.method === "view";
    },
    isCreateMethod: function () {
      return this.$route.meta.method === "create";
    },
    isUpdateMethod: function () {
      return this.$route.meta.method === "update";
    },
    isDuplicateMethod: function () {
      return this.$route.meta.method === "duplicate";
    },
    getPageTile: function () {
      return this.$t("project." + this.$route.meta.method + ".title");
    },
    getActionButtons: function () {
      let buttons = [];

      if (this.$store.getters.userHasPermission("project_create")) {
        buttons.push({
          action: "save-finally",
        });
      }

      buttons.push({
        action: "cancel",
        router: {
          name: "customers-view-customer-projects",
          params: { customer: this.$route.params.customer },
        },
      });

      return buttons;
    },
  },
  watch: {
    $route: {
      handler: function () {
        this.onUpdateObject();
        this.onUpdateLayers();
      },
      deep: true,
    },
    "object.layers": {
      handler: function (newVal) {
        this.onUpdateLayersStats();
      },
      deep: true,
    },
  },
  created: function () {
    this.onUpdateObject();
    this.onUpdateLayers();
  },
  validations: function () {
    return {
      object: {
        location_id: {
          required,
          isValidNumber: (value) => {
            return parseInt(value) !== NaN && parseInt(value) >= 1;
          },
        },
        account_id: {
          required,
          isValidNumber: (value) => {
            return parseInt(value) !== NaN && parseInt(value) >= 1;
          },
        },
        internal_number: {
           required,
        },
        earliest_start: {
          required,
          isAllowed: (value) => {
            return this.$helpers.isEarliestStartAllowed(value);
          },
        },
        address: {
          zipcode: {
            required,
          },
          city: {
            required,
          },
          street: {
            required,
          },
        },
        layers: {
          $each: {
            placement_type: {
              required,
            },
            subtype: {
              required,
            },
            qm: {
              required,
              isValidNumber: (value) => {
                return parseInt(value) !== NaN && parseInt(value) >= 1 && value % 1 === 0;
              },
            },
            thickness: {
              required,
              isValidNumber: (value) => {
                return parseInt(value) !== NaN && parseInt(value) >= 1 && value % 1 === 0;
              },
            },
            layer_logs: {},
          },
        },
      },
    };
  },
  methods: {
    sortLayersBySortOrder: function() {
      this.sortLayers = this.layers.slice(0);
      this.sortLayers.sort(function(a,b) {
          return a.sort_order_frontend - b.sort_order_frontend;
      });
    },
    
    onUpdateObject: function () {
      this.loadingError = null;
      this.loading = false;
    },
    onUpdateLayers: function () {
      let promise = this.$api.clearParams().get("layertypes");

      promise
        .then(
          ((response) => {
            this.layers = response.data.data;
            this.sortLayersBySortOrder();
          }).bind(this)
        )
        .catch(((error) => {}).bind(this))
        .finally(
          (() => {
            this.layersLoading = false;
          }).bind(this)
        );

      this.layersLoading = true;
    },
    onSubmitProjectObject: function () {
      this.projectFormDisabled = true;
      this.projectObjectError = null;
      this.projectObjectSuccess = null;

      this.$v.object.$touch();

      if (this.$v.object.$invalid) {
        this.projectFormDisabled = false;
        this.projectObjectError = this.$t("form.error_form.projectcrud");
      } else if (this.layersStats.open === 0) {
        this.projectFormDisabled = false;
        this.projectObjectError = this.$t("form.error_form.nosquaremeters");
      } else if (
        !this.object.layer_tok &&
        this.objectCheckTOKEmptyWanted === null
      ) {
        this.projectFormDisabled = false;
        this.objectCheckTOK = this.$t("form.project.error.checktok");
      } else {
        let promise = this.$api
          .clearParams()
          .post("projects/create", this.$helpers.projectPayload(this.object));

        promise
          .then(
            ((response) => {
              this.projectFormDisabled = false;
              this.projectObjectSuccess = this.$t("project.create.success");

              this.$router.push({
                name: "customers-update-customer-project",
                params: {
                  customer: response.data.data.customer_id,
                  project: response.data.data.id,
                },
              });
            }).bind(this)
          )
          .catch(
            ((error) => {
              this.projectFormDisabled = false;
              this.projectObjectError = error.response.data.message;
            }).bind(this)
          );
      }
    },

    getMarker: function (marker) {
      if (marker) {
        this.object.lat = marker.lat;
        this.object.lon = marker.lng;
      }
    },
    onClickAction: function (action) {
      this.projectObjectError = null;
      this.projectObjectSuccess = null;
      this.projectObjectActivate = null;

      switch (action) {
        case "save":
        case "save-finally":
          this.onSubmitProjectObject();

          break;
        case "activate":
        case "deactivate":
          this.onActivateProjectObject();

          break;
      }
    },

    onClickDialogAction: function (id, action) {
      this.projectObjectError = null;
      this.projectObjectSuccess = null;
      this.projectObjectActivate = null;

      switch (action) {
        case "yes":
          this.objectCheckTOKEmptyWanted = true;
          this.onSubmitProjectObject();
          break;
        case "no":
          this.objectCheckTOKEmptyWanted = false;
          this.object.layer_tok = true;
          this.onSubmitProjectObject();
          break;
      }
    },

    onChangeLocation: function (location) {
      if (location === null) {
        this.object.account_id = 0;
        this.object.location.project_number_required = false;
        this.object.location.risk = null;
      } else {
        this.object.location.project_number_required =
          location.project_number_required;
        if (location.risk) {
          this.object.location.risk = location.risk;
        } else {
          this.object.location.risk = null;
        }
      }
    },
    onChangeAccount: function (account) {
      if (account === null) {
        this.object.account.profile.communication.email = "";
        this.object.account.profile.communication.mobile = "";
        this.object.account.profile.communication.phone = "";
      } else {
        this.object.account.profile.communication.email =
          account.profile.communication.email;
        this.object.account.profile.communication.mobile =
          account.profile.communication.mobile;
        this.object.account.profile.communication.phone =
          account.profile.communication.phone;
      }
    },
    onChangeLayerInstallation: function () {
      if (!this.layer_installation) {
        this.object.layer_width_min = 0;
        this.object.layer_width_max = 0;
      }
    },
    onChangeLayer: function (visible, layerId, object) {
      if (visible) {
        if (!this.object.layers[layerId]) {
          this.object.layers[layerId] = object;
        }
      } else {
        if (this.object.layers[layerId]) {
          delete this.object.layers[layerId];
        }
      }

      this.object.layers = JSON.parse(JSON.stringify(this.object.layers));
    },
    onUpdateLayersStats: function () {
      this.layersStats.total = 0;
      this.layersStats.finished = 0;
      this.layersStats.open = 0;
      this.layersStats.layers.default = 0;
      this.layersStats.layers.addition = 0;

      for (var key in this.object.layers) {
        var layer = this.object.layers[key];

        if (layer) {
          if (layer.qm && parseInt(layer.qm)) {
            this.layersStats.total += parseInt(layer.qm);

            if (layer.layer_type.is_default) {
              this.layersStats.layers.default += parseInt(layer.qm);
            } else {
              this.layersStats.layers.addition += parseInt(layer.qm);
            }
          }

          if (layer.layer_logs) {
            layer.layer_logs.forEach(
              function (log) {
                this.layersStats.finished += parseInt(log.qm);
              }.bind(this)
            );
          }
        }
      }

      this.layersStats.open =
        this.layersStats.total - this.layersStats.finished;
    },
    onChangeZipcode: _.debounce(function (event) {
      if (event.target.value !== "") {
        this.getGeocodeResult(event.target.value, "zipcode", "city");
      }
    }, 500),
    onChangeCity: _.debounce(function (event) {
      if (event.target.value !== "") {
        this.getGeocodeResult(event.target.value, "city", "zipcode");
      }
    }, 500),
    onSelectZipcode: function (event) {
      this.object.address.zipcode = event.target.value;

      this.geocodeObject.zipcode = [];
    },
    onSelectCity: function (event) {
      this.object.address.city = event.target.value;

      this.geocodeObject.city = [];
    },
    getGeocodeResult: function (value, type, target) {
        let promise = this.$geocoder.search(value, type);

        promise
        .then(
            ((response) => {
            let results = [];

            response.data.data.forEach(
                function (row) {
                results.push(row);
                }.bind(this)
            );

            if (results.length === 1) {
                this.object.address[target] = results[0][target];
                
                // Close an open zipcode dropdown
                this.geocodeObject[target] = [];
            } else {
                this.geocodeObject[target] = results;
            }

            this.geocodeObject[type] = [];
            }).bind(this)
        )
        .catch(
            ((error) => {
            this.geocodeObject[target] = [];
            this.geocodeObject[type] = [];
            }).bind(this)
        )
        .finally(
            (() => {
            this.geocodeLoading = false;
            }).bind(this)
        );

        this.geocodeLoading = true;
    },
  },
};
</script>